// src/pages/ThermalMapping.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ShelfHeatmap from "../components/ShelfHeatmap.js";
import "./ThermalMapping.css";

// Para las gráficas
import { Line } from "react-chartjs-2";
import "chart.js/auto";

/** ===== Config ===== */
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores`;
const FRESH_MINUTES = 10;

// RANGO “OK” por defecto (normales indoor)
const DEFAULT_LIMITS_TEMP = { minOk: 20, maxOk: 28 }; // °C
const DEFAULT_LIMITS_HUM = { minOk: 40, maxOk: 60 }; // %HR

const SHELF_BG = `${process.env.PUBLIC_URL || ""}/estanteria.png`;

/** ===== Posiciones (tus coordenadas) ===== */
const ABS_POS_SUP = [
  { left: 9.840816326530613, top: 18.2421768707483, width: 25.2, height: 15.6 },
  { left: 37.59591836734693, top: 18.42358276643991, width: 25.2, height: 15.6 },
  { left: 64.94285714285714, top: 17.87936507936508, width: 25.2, height: 15.6 },
  { left: 9.942857142857143, top: 35.18140589569161, width: 25.2, height: 15.6 },
  { left: 37.8, top: 35, width: 25.2, height: 15.6 },
  { left: 64.73877551020408, top: 36.26984126984127, width: 25.2, height: 15.6 },
  { left: 9.738775510204082, top: 54.11609977324263, width: 25.2, height: 15.6 },
  { left: 37.8, top: 54.11609977324263, width: 25.2, height: 15.6 },
  { left: 64.94285714285714, top: 54.478911564625854, width: 25.2, height: 15.6 },
  { left: 9.636734693877552, top: 72.68798185941044, width: 25.2, height: 15.6 },
  { left: 37.391836734693875, top: 72.86938775510205, width: 25.2, height: 15.6 },
  { left: 64.73877551020408, top: 72.50657596371882, width: 25.2, height: 15.6 },
];

const ABS_POS_MED = [
  { left: 10.146938775510204, top: 17.15374149659864, width: 25.2, height: 15.6 },
  { left: 37.8, top: 16.4281179138322, width: 25.2, height: 15.6 },
  { left: 65.1469387755102, top: 16.60952380952381, width: 25.2, height: 15.6 },
  { left: 9.942857142857143, top: 36.08843537414966, width: 25.2, height: 15.6 },
  { left: 37.8, top: 35, width: 25.2, height: 15.6 },
  { left: 64.94285714285714, top: 35.36281179138322, width: 25.2, height: 15.6 },
  { left: 10.044897959183674, top: 53.571882086167804, width: 25.2, height: 15.6 },
  { left: 37.59591836734693, top: 53.93469387755102, width: 25.2, height: 15.6 },
  { left: 64.73877551020408, top: 54.11609977324263, width: 25.2, height: 15.6 },
  { left: 9.738775510204082, top: 72.32517006802722, width: 25.2, height: 15.6 },
  { left: 37.289795918367346, top: 72.50657596371882, width: 25.2, height: 15.6 },
  { left: 64.94285714285715, top: 72.3251700680272, width: 25.2, height: 15.6 },
];

const ABS_POS_INF = [
  { left: 9.942857142857143, top: 16.60952380952381, width: 25.2, height: 15.6 },
  { left: 37.69795918367347, top: 16.79092970521542, width: 25.2, height: 15.6 },
  { left: 65.04489795918367, top: 17.33514739229025, width: 25.2, height: 15.6 },
  { left: 9.942857142857141, top: 35.72562358276644, width: 25.2, height: 15.6 },
  { left: 37.8, top: 35, width: 25.2, height: 15.6 },
  { left: 64.84081632653061, top: 35.72562358276644, width: 25.2, height: 15.6 },
  { left: 9.738775510204082, top: 53.753287981859415, width: 25.2, height: 15.6 },
  { left: 37.8, top: 54.29750566893424, width: 25.2, height: 15.6 },
  { left: 65.04489795918367, top: 53.39047619047619, width: 25.2, height: 15.6 },
  { left: 10.044897959183674, top: 72.68798185941044, width: 25.2, height: 15.6 },
  { left: 37.289795918367346, top: 72.68798185941044, width: 25.2, height: 15.6 },
  { left: 64.94285714285714, top: 72.50657596371883, width: 25.2, height: 15.6 },
];

/** ===== Sensores por posición ===== */
const DEVICE_POSITIONS = {
  superior: [
    "24e124723e352431",
    "24e124725f313433",
    "24e124725f312960",
    "24e124725f312984",
    "24e124725f313347",
    "24e124725f312957",
    "24e124725f313322",
    "24e124725f313358",
    "24e124725f313389",
    "24e124725f313376",
    "24e124725f312977",
    null,
  ],
  medio: [null, null, null, null, null, null, null, null, null, null, null, null],
  inferior: [null, null, null, null, null, null, null, null, null, null, null, null],
};

// Límites por sensor (opcional)
const DEVICE_LIMITS_TEMP = {};
const DEVICE_LIMITS_HUM = {};

/** ===== Helpers ===== */
const authHeaders = () => {
  try {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
};

const stale = (ts) =>
  Date.now() - new Date(ts).getTime() > FRESH_MINUTES * 60 * 1000;

// Aliases de variables
const TEMP_ALIASES = [
  "TEMP",
  "temperature",
  "temp",
  "temp_c",
  "Temperature",
  "TEMPERATURA",
];
const HUM_ALIASES = ["HUM", "humidity", "humedad", "hum", "rh", "RH"];

/** ===== Data: último valor ===== */
async function fetchLatestVar(deviceId, aliases) {
  for (const variable of aliases) {
    try {
      const url = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
        deviceId
      )}&variable=${encodeURIComponent(variable)}&limit=1`;
      const { data } = await axios.get(url, {
        headers: authHeaders(),
        withCredentials: false,
      });
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) continue;

      const value =
        row.value ?? row.valor ?? row.val ?? row[variable] ?? null;
      const ts = row.ts || row.fecha || row.time || row.timestamp || null;

      if (value != null && Number.isFinite(Number(value))) {
        return { value: Number(value), ts };
      }
    } catch {
      // probar siguiente alias
    }
  }
  return { value: null, ts: null };
}

async function fetchLatestPair(deviceId) {
  const [temp, hum] = await Promise.all([
    fetchLatestVar(deviceId, TEMP_ALIASES),
    fetchLatestVar(deviceId, HUM_ALIASES),
  ]);
  return { temp, hum };
}

/** ===== Data: histórico para gráficas ===== */
async function fetchHistorySeries(deviceId, aliases, limit = 12) {
  for (const variable of aliases) {
    try {
      const url = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
        deviceId
      )}&variable=${encodeURIComponent(variable)}&limit=${limit}`;
      const { data } = await axios.get(url, {
        headers: authHeaders(),
        withCredentials: false,
      });
      const arr = Array.isArray(data) ? data : [];
      if (!arr.length) continue;

      const points = arr
        .map((row) => {
          const raw =
            row.value ?? row.valor ?? row.val ?? row[variable] ?? null;
          const ts = row.ts || row.fecha || row.time || row.timestamp || null;
          if (raw == null || ts == null) return null;
          const num = Number(raw);
          if (!Number.isFinite(num)) return null;
          return { value: num, ts };
        })
        .filter(Boolean);

      if (points.length) return points;
    } catch {
      // probar siguiente alias
    }
  }
  return [];
}

async function fetchHistoryPair(deviceId, limit = 12) {
  const [temp, hum] = await Promise.all([
    fetchHistorySeries(deviceId, TEMP_ALIASES, limit),
    fetchHistorySeries(deviceId, HUM_ALIASES, limit),
  ]);
  return { temp, hum };
}

/** ===== UI helpers ===== */
function computeTileClass(reading, tempLimits, humLimits) {
  const t = reading?.temp?.value;
  const tt = reading?.temp?.ts;
  const h = reading?.hum?.value;
  const th = reading?.hum?.ts;

  const freshT = t != null && tt && !stale(tt);
  const freshH = h != null && th && !stale(th);

  if (!freshT && !freshH) return "tile offline";
  if (freshT && (t < tempLimits.minOk || t > tempLimits.maxOk)) return "tile alarm";
  if (freshH && (h < humLimits.minOk || h > humLimits.maxOk)) return "tile alarm";
  return "tile ok";
}

const fmtC = (v) =>
  v == null || Number.isNaN(v) ? "-" : `${Number(v).toFixed(1)} °C`;
const fmtH = (v) =>
  v == null || Number.isNaN(v) ? "-" : `${Number(v).toFixed(0)} %HR`;

function AbsTile({ index, deviceId, reading, tempLimits, humLimits, rect }) {
  const { temp, hum } = reading || { temp: null, hum: null };
  const klass = computeTileClass(reading, tempLimits, humLimits);
  const label = deviceId || `Pos ${index + 1}`;
  return (
    <div
      className={`tile tile-abs ${klass}`}
      style={{
        left: `${rect.left}%`,
        top: `${rect.top}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`,
      }}
    >
      <div className="tile-value">
        {fmtC(temp?.value)} | {fmtH(hum?.value)}
      </div>
      <div className="tile-label">{label}</div>
    </div>
  );
}

function ShelfFixed({
  title,
  rects,
  deviceIds,
  readings,
  limitsTempById,
  limitsHumById,
}) {
  return (
    <div className="panel">
      <div className="panel-title">{title}</div>

      <div
        className="shelf-abs"
        style={{ backgroundImage: `url('${SHELF_BG}')` }}
      >
        {/* Overlay: izquierda=T°, derecha=%HR */}
        <ShelfHeatmap
          rects={rects}
          deviceIds={deviceIds}
          readings={readings}
          limitsTempById={limitsTempById}
          limitsHumById={limitsHumById}
          defaultTempLimits={DEFAULT_LIMITS_TEMP}
          defaultHumLimits={DEFAULT_LIMITS_HUM}
          okColorTemp="#2563eb" // azul T° OK
          warnColorTemp="#f59e0b" // amarillo T° fuera
          okColorHum="#22c55e" // verde %HR OK
          warnColorHum="#ef4444" // rojo %HR fuera
          cellOpacity={0.6}
          splitGapPct={0} // sin línea blanca al centro
          autoFromTiles={true}
          // Padding más ceñido y con "bleed" en bordes exteriores
          cellPadding={(i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            const pad = { top: 3.2, right: 0.9, bottom: 3.2, left: 0.9 };
            if (col === 0) pad.left = 0.3;
            if (col === 2) pad.right = 0.3;
            if (row === 0) pad.top = 2.6;
            if (row === 3) pad.bottom = 2.4;
            return pad;
          }}
          cellBorderRadius={1}
          staleMinutes={10}
        />

        {/* Tarjetas con lectura T° | %HR */}
        {rects.map((rect, i) => {
          const deviceId = deviceIds[i] || null;
          const tempLimits = deviceId
            ? limitsTempById[deviceId] || DEFAULT_LIMITS_TEMP
            : DEFAULT_LIMITS_TEMP;
          const humLimits = deviceId
            ? limitsHumById[deviceId] || DEFAULT_LIMITS_HUM
            : DEFAULT_LIMITS_HUM;
          return (
            <AbsTile
              key={`${title}-${i}`}
              index={i}
              deviceId={deviceId}
              reading={deviceId ? readings[deviceId] : null}
              tempLimits={tempLimits}
              humLimits={humLimits}
              rect={rect}
            />
          );
        })}
      </div>
    </div>
  );
}

/** ===== Vista de gráficas ===== */
function ChartsView({ devicePositions, historyByDevice, loading }) {
  const navigate = useNavigate();
  const COMBINED_ID = "COMBINED";

  // IDs reales de sensores
  const sensorIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...devicePositions.superior,
            ...devicePositions.medio,
            ...devicePositions.inferior,
          ].filter(Boolean)
        )
      ),
    [devicePositions]
  );

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!selected && sensorIds.length) {
      setSelected(sensorIds[0]); // por defecto el primer sensor
    }
  }, [sensorIds, selected]);

  if (!sensorIds.length) {
    return <p>No hay sensores configurados para el mapeo.</p>;
  }

  const isCombined = selected === COMBINED_ID;

  // ===== Colores para las series combinadas =====
  const SERIES_COLORS = [
    "rgba(248,113,113,1)", // rojo
    "rgba(59,130,246,1)", // azul
    "rgba(16,185,129,1)", // verde esmeralda
    "rgba(234,179,8,1)", // amarillo
    "rgba(249,115,22,1)", // naranja
    "rgba(139,92,246,1)", // violeta
    "rgba(236,72,153,1)", // rosa
    "rgba(56,189,248,1)", // celeste
    "rgba(34,197,94,1)", // verde
    "rgba(37,99,235,1)", // índigo
    "rgba(245,158,11,1)", // ámbar
  ];

  // ===== OPCIONES BASE DE LOS GRÁFICOS =====
  const commonOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: "#111827",
          font: { size: 11, weight: "600" },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Hora", color: "#374151" },
        ticks: {
          color: "#6b7280",
          font: { size: 10 },
        },
        grid: {
          color: "rgba(209, 213, 219, 0.6)",
        },
      },
      y: {
        title: { display: true, text: "", color: "#374151" },
        ticks: {
          color: "#6b7280",
          font: { size: 10 },
        },
        grid: {
          color: "rgba(209, 213, 219, 0.6)",
        },
      },
    },
  };

  // =====================================================
  //   VISTA POR SENSOR (uno solo)
  // =====================================================
  let tempDataSingle = null;
  let humDataSingle = null;
  let hasSingleTemp = false;
  let hasSingleHum = false;

  if (!isCombined && selected) {
    const h = historyByDevice[selected] || {};
    const tempPoints = h.temp || [];
    const humPoints = h.hum || [];

    hasSingleTemp = tempPoints.length > 0;
    hasSingleHum = humPoints.length > 0;

    tempDataSingle = {
      labels: tempPoints.map((p) =>
        new Date(p.ts).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      ),
      datasets: [
        {
          label: "Temperatura (°C)",
          data: tempPoints.map((p) => p.value),
          borderWidth: 3,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderColor: "rgba(248, 113, 113, 1)", // rojo fuerte
          backgroundColor: "rgba(248, 113, 113, 0.15)",
          pointBackgroundColor: "rgba(248, 113, 113, 1)",
          pointBorderColor: "#ffffff",
          fill: true,
        },
      ],
    };

    humDataSingle = {
      labels: humPoints.map((p) =>
        new Date(p.ts).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      ),
      datasets: [
        {
          label: "Humedad relativa (%HR)",
          data: humPoints.map((p) => p.value),
          borderWidth: 3,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderColor: "rgba(56, 189, 248, 1)", // celeste fuerte
          backgroundColor: "rgba(56, 189, 248, 0.15)",
          pointBackgroundColor: "rgba(56, 189, 248, 1)",
          pointBorderColor: "#ffffff",
          fill: true,
        },
      ],
    };
  }

  // =====================================================
  //   VISTA COMBINADA (todos los sensores)
  // =====================================================
  let combinedTempData = null;
  let combinedHumData = null;
  let hasCombinedTemp = false;
  let hasCombinedHum = false;

  if (isCombined) {
    // Sensor de referencia para TEMP (primero que tenga datos)
    const refIdTemp =
      sensorIds.find((id) => (historyByDevice[id]?.temp || []).length) ||
      sensorIds[0];
    // Sensor de referencia para HUM (primero que tenga datos)
    const refIdHum =
      sensorIds.find((id) => (historyByDevice[id]?.hum || []).length) ||
      sensorIds[0];

    const refTempPoints = (historyByDevice[refIdTemp] || {}).temp || [];
    const refHumPoints = (historyByDevice[refIdHum] || {}).hum || [];

    const labelsTemp = refTempPoints.map((p) =>
      new Date(p.ts).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
    const labelsHum = refHumPoints.map((p) =>
      new Date(p.ts).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );

    combinedTempData = {
      labels: labelsTemp,
      datasets: sensorIds.map((id, idx) => {
        const hp = (historyByDevice[id] || {}).temp || [];
        const data = labelsTemp.map((_, i) => (hp[i] ? hp[i].value : null));
        const color = SERIES_COLORS[idx % SERIES_COLORS.length];
        if (data.some((v) => v !== null)) hasCombinedTemp = true;
        return {
          label: id,
          data,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 0,
          borderColor: color,
          backgroundColor: "transparent",
        };
      }),
    };

    combinedHumData = {
      labels: labelsHum,
      datasets: sensorIds.map((id, idx) => {
        const hp = (historyByDevice[id] || {}).hum || [];
        const data = labelsHum.map((_, i) => (hp[i] ? hp[i].value : null));
        const color = SERIES_COLORS[idx % SERIES_COLORS.length];
        if (data.some((v) => v !== null)) hasCombinedHum = true;
        return {
          label: id,
          data,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 0,
          borderColor: color,
          backgroundColor: "transparent",
        };
      }),
    };
  }

  // ===== Botón de reporte =====
  const handleGenerateReport = () => {
    const type = "HumedadTemperatura";
    const base = `/report/${encodeURIComponent(type)}`;

    // Si estás en "Combinado", no mandamos sensorId (que elijas ahí).
    // Si estás en un sensor específico, lo pasamos en el query.
    const sensorId =
      !selected || selected === COMBINED_ID ? "" : encodeURIComponent(selected);

    const url = sensorId ? `${base}?sensorId=${sensorId}` : base;
    navigate(url);
  };

  const buttonItems = [...sensorIds, COMBINED_ID];

  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Encabezado + botón de reporte */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <h3 style={{ fontSize: 18, margin: 0 }}>Histórico de sensores</h3>
        <button
          onClick={handleGenerateReport}
          style={{
            padding: "8px 16px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: "#0f766e",
            color: "#ffffff",
            boxShadow: "0 8px 16px rgba(15, 118, 110, 0.25)",
          }}
        >
          Generar reporte
        </button>
      </div>

      {/* Lista de sensores + gráficas */}
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* Lista de sensores */}
        <div style={{ minWidth: 220, flex: "0 0 auto" }}>
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>Sensores</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {buttonItems.map((id) => (
              <button
                key={id}
                onClick={() => setSelected(id)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  backgroundColor: selected === id ? "#0f766e" : "#e5e7eb",
                  color: selected === id ? "#ffffff" : "#111827",
                  textAlign: "left",
                }}
              >
                {id === COMBINED_ID ? "Combinado" : id}
              </button>
            ))}
          </div>
        </div>

        {/* Gráficas */}
        <div
          style={{
            flex: "1 1 300px",
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          {/* Temperatura */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              padding: 12,
              boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
            }}
          >
            <h3 style={{ fontSize: 14, marginBottom: 8 }}>Temperatura</h3>
            {loading ? (
              <p>Cargando datos...</p>
            ) : isCombined ? (
              hasCombinedTemp ? (
                <Line
                  data={combinedTempData}
                  options={{
                    ...commonOptions,
                    scales: {
                      ...commonOptions.scales,
                      y: {
                        ...commonOptions.scales.y,
                        title: { display: true, text: "°C", color: "#374151" },
                      },
                    },
                  }}
                />
              ) : (
                <p>Sin datos de temperatura.</p>
              )
            ) : hasSingleTemp ? (
              <Line
                data={tempDataSingle}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      title: { display: true, text: "°C", color: "#374151" },
                    },
                  },
                }}
              />
            ) : (
              <p>Sin datos de temperatura.</p>
            )}
          </div>

          {/* Humedad */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              padding: 12,
              boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
            }}
          >
            <h3 style={{ fontSize: 14, marginBottom: 8 }}>Humedad relativa</h3>
            {loading ? (
              <p>Cargando datos...</p>
            ) : isCombined ? (
              hasCombinedHum ? (
                <Line
                  data={combinedHumData}
                  options={{
                    ...commonOptions,
                    scales: {
                      ...commonOptions.scales,
                      y: {
                        ...commonOptions.scales.y,
                        title: { display: true, text: "%HR", color: "#374151" },
                      },
                    },
                  }}
                />
              ) : (
                <p>Sin datos de humedad.</p>
              )
            ) : hasSingleHum ? (
              <Line
                data={humDataSingle}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      title: { display: true, text: "%HR", color: "#374151" },
                    },
                  },
                }}
              />
            ) : (
              <p>Sin datos de humedad.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** ===== Componente principal ===== */
export default function ThermalMapping() {
  const navigate = useNavigate();

  const [view, setView] = useState("map"); // 'map' | 'charts'
  const [readings, setReadings] = useState({});
  const [history, setHistory] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);

  const limitsTempById = useMemo(() => DEVICE_LIMITS_TEMP, []);
  const limitsHumById = useMemo(() => DEVICE_LIMITS_HUM, []);

  const allDeviceIds = useMemo(() => {
    const flat = [
      ...DEVICE_POSITIONS.superior,
      ...DEVICE_POSITIONS.medio,
      ...DEVICE_POSITIONS.inferior,
    ].filter(Boolean);
    return Array.from(new Set(flat));
  }, []);

  // Carga de último valor (vista mapa)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!allDeviceIds.length) return;
      try {
        const pairs = await Promise.all(
          allDeviceIds.map(async (id) => {
            try {
              const r = await fetchLatestPair(id);
              return [id, r];
            } catch (err) {
              if (axios.isAxiosError(err)) {
                const st = err.response?.status;
                if (st === 401) navigate("/login");
                if (st === 403)
                  console.warn("403: revisar permisos/user_sensors");
              }
              return [
                id,
                { temp: { value: null, ts: null }, hum: { value: null, ts: null } },
              ];
            }
          })
        );
        if (!cancelled) {
          const next = {};
          for (const [id, r] of pairs) next[id] = r;
          setReadings(next);
        }
      } catch (e) {
        console.error("Error mapeo térmico:", e);
      }
    }
    load();
    const iv = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [allDeviceIds, navigate]);

  // Carga de histórico (vista gráficas)
  useEffect(() => {
    if (view !== "charts") return;
    if (!allDeviceIds.length) return;

    let cancelled = false;
    async function loadHistory() {
      setHistoryLoading(true);
      try {
        const pairs = await Promise.all(
          allDeviceIds.map(async (id) => {
            try {
              const series = await fetchHistoryPair(id, 12);
              return [id, series];
            } catch (err) {
              if (axios.isAxiosError(err)) {
                const st = err.response?.status;
                if (st === 401) navigate("/login");
                if (st === 403)
                  console.warn("403 histórico: revisar permisos/user_sensors");
              }
              return [id, { temp: [], hum: [] }];
            }
          })
        );
        if (!cancelled) {
          const next = {};
          for (const [id, series] of pairs) next[id] = series;
          setHistory(next);
        }
      } catch (e) {
        console.error("Error cargando histórico mapeo térmico:", e);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    }

    loadHistory();
    const iv = setInterval(loadHistory, 60_000);

    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [view, allDeviceIds, navigate]);

  return (
    <div className="tm-root">
      <h2 style={{ marginBottom: 8 }}>Mapeo Térmico</h2>

      {/* Pestañas */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setView("map")}
          style={{
            padding: "6px 12px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: view === "map" ? "#0f766e" : "#e5e7eb",
            color: view === "map" ? "#ffffff" : "#111827",
          }}
        >
          Mapa térmico
        </button>
        <button
          onClick={() => setView("charts")}
          style={{
            padding: "6px 12px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: view === "charts" ? "#0f766e" : "#e5e7eb",
            color: view === "charts" ? "#ffffff" : "#111827",
          }}
        >
          Gráficas
        </button>
      </div>

      {view === "map" && (
        <>
          <ShelfFixed
            title="Superior · Frontal"
            rects={ABS_POS_SUP}
            deviceIds={DEVICE_POSITIONS.superior}
            readings={readings}
            limitsTempById={limitsTempById}
            limitsHumById={limitsHumById}
          />
          <ShelfFixed
            title="Medio · Frontal"
            rects={ABS_POS_MED}
            deviceIds={DEVICE_POSITIONS.medio}
            readings={readings}
            limitsTempById={limitsTempById}
            limitsHumById={limitsHumById}
          />
          <ShelfFixed
            title="Inferior · Frontal"
            rects={ABS_POS_INF}
            deviceIds={DEVICE_POSITIONS.inferior}
            readings={readings}
            limitsTempById={limitsTempById}
            limitsHumById={limitsHumById}
          />

          <div
            style={{
              fontSize: 12,
              opacity: 0.8,
              marginTop: 8,
            }}
          >
            <span className="legend ok">T° OK = Azul</span>{" "}
            <span className="legend alarm">T° fuera = Amarillo</span>{" "}
            <span className="legend ok" style={{ background: "#22c55e" }}>
              HR OK = Verde
            </span>{" "}
            <span className="legend alarm" style={{ background: "#ef4444" }}>
              HR fuera = Rojo
            </span>{" "}
            <span className="legend offline">Sin dato reciente</span>{" "}
            <span style={{ marginLeft: 12, opacity: 0.9 }}>
              <b>Mitades:</b> Izq = <b>T°</b>, Der = <b>%HR</b>
            </span>
          </div>
        </>
      )}

      {view === "charts" && (
        <ChartsView
          devicePositions={DEVICE_POSITIONS}
          historyByDevice={history}
          loading={historyLoading}
        />
      )}
    </div>
  );
}
